import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FileDown,
  SquarePlus,
  Settings,
  StickyNote,
  Trash2,
} from "lucide-react";
import {
  getViewportForBounds,
  useReactFlow,
  useStore,
  useViewport,
} from "@xyflow/react";
import type * as Y from "yjs";
import type { WidgetKind, SolvePhase } from "@playbookai/types";
import { getSolveWidgetPlacementLimit } from "@playbookai/types";
import {
  ActionMenu,
  CANVAS_WORKSHOP_CONTROL_THRESHOLDS,
  WorkspaceControlButton,
  WorkspaceControlGroup,
  WorkspaceControlSlot,
  WorkspaceViewportControls,
  WorkspaceWidgetPicker,
  addStickyNote,
  getWidget,
  useCameraDuration,
  type ActionMenuItem,
  type WorkspaceControlDensity,
  type WorkspaceWidgetPickerItem,
} from "@playbookai/ui";
import { widgetsMap } from "@playbookai/collab-client";
import { addWidget } from "./add-widget.js";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  SOLVE_MAX_ZOOM,
  SOLVE_MIN_ZOOM,
} from "./phase-metadata.js";
import {
  SOLVE_DEFAULT_PHASE,
  SOLVE_WIDGET_KINDS,
  SOLVE_WIDGET_MODULE,
} from "../widgets/phase-mapping.js";
import { useApiClient } from "../api/context.js";
import { useModuleAvailability } from "@playbookai/api-clients";
import { notifyWriteError } from "../lib/notify-content-lock.js";
import {
  PlaybookApiError,
  type PlacementResponse,
} from "../api/playbook-be-client.js";
import { solveWidgetsQueryKey } from "../hooks/useSolveWidgets.js";

interface SolveCanvasToolbarProps {
  readonly doc: Y.Doc;
  readonly solveId: string;
  readonly activePhase: SolvePhase;
  readonly canvasWidth?: number;
  /** Live canvas height — grows with a resized phase area (`phase-areas.ts`). */
  readonly canvasHeight?: number;
  readonly onWidgetPlaced?: (placement: PlacementResponse) => void;
  readonly miniMapVisible: boolean;
  readonly miniMapAvailable?: boolean;
  readonly onToggleMiniMap: () => void;
  readonly presenceSlot?: ReactNode;
  readonly peopleSlot?: ReactNode;
  readonly onOpenSettings?: () => void;
  /**
   * Open the Export PDF review dialog. Absent for a caller that cannot read
   * the report — the control is not rendered at all rather than disabled,
   * because a member without `view` has no Solve to export.
   */
  readonly onOpenExport?: () => void;
  readonly onDelete?: () => void;
  readonly areaGrowth?: Parameters<typeof addWidget>[6];
  readonly canAddWidgets?: boolean;
  readonly canAddToPhase?: (phase: SolvePhase | "any") => boolean;
}

const ZOOM_STEP = 0.1;
const FIT_TOP_PADDING = "72px";
const FIT_EDGE_PADDING = "8%";
const SOLVE_WIDGET_KIND_SET: ReadonlySet<WidgetKind> = new Set(
  SOLVE_WIDGET_KINDS,
);
const SOLVE_ACTION_THRESHOLDS = {
  ...CANVAS_WORKSHOP_CONTROL_THRESHOLDS,
  stackedPhoneAt: 640,
} as const;

/** SOLVE top-end actions plus the bottom-start viewport cluster. */
export function SolveCanvasToolbar({
  doc,
  solveId,
  activePhase,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
  onWidgetPlaced,
  miniMapVisible,
  miniMapAvailable = true,
  onToggleMiniMap,
  presenceSlot,
  peopleSlot,
  onOpenSettings = () => undefined,
  onOpenExport,
  onDelete,
  areaGrowth,
  canAddWidgets = true,
  canAddToPhase = () => true,
}: SolveCanvasToolbarProps): ReactElement {
  const wmap = widgetsMap(doc);
  const [version, force] = useState(0);
  const [density, setDensity] = useState<WorkspaceControlDensity>({
    labels: "visible",
    stage: "pill",
    layout: "single-row",
    foldLevel: 0,
    availableWidth: Number.POSITIVE_INFINITY,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const rf = useReactFlow();
  /* Camera timing comes from the hook, not from a constant: ReactFlow animates
     the viewport in JavaScript, so the `prefers-reduced-motion` block in
     `theme.css` never reaches it and only this can return 0 (ADR-0091).
     Revealing a placed widget and fitting the whole canvas are `framing`; a
     zoom button is one discrete `step`. */
  const framingDuration = useCameraDuration("framing");
  const zoomStepDuration = useCameraDuration("step");
  const paneWidth = useStore((state) => state.width);
  const paneHeight = useStore((state) => state.height);
  const { x, y, zoom } = useViewport();
  const apiConfig = useApiClient();
  const availability = useModuleAvailability();
  const queryClient = useQueryClient();
  const focusRequest = useRef(0);
  const pendingSingletonKindsRef = useRef(new Set<WidgetKind>());
  const [pendingSingletonKinds, setPendingSingletonKinds] = useState<
    ReadonlySet<WidgetKind>
  >(new Set());

  useEffect(() => {
    // Ignore pending camera work after leaving this SOLVE or phase.
    return () => {
      focusRequest.current += 1;
    };
  }, [doc, activePhase]);

  useEffect(() => {
    const observe = (): void => force((value) => value + 1);
    wmap.observe(observe);
    return (): void => wmap.unobserve(observe);
  }, [wmap]);

  const pickerItems = useMemo<readonly WorkspaceWidgetPickerItem[]>(() => {
    const presentKinds = new Set<WidgetKind>();
    wmap.forEach((map) => {
      const kind = map.get("kind");
      if (
        typeof kind === "string" &&
        SOLVE_WIDGET_KIND_SET.has(kind as WidgetKind)
      ) {
        presentKinds.add(kind as WidgetKind);
      }
    });
    return SOLVE_WIDGET_KINDS.flatMap((kind) => {
      const definition = getWidget(kind);
      // A widget that shows an off module's data is not offered (ADR-0100);
      // one already on the canvas stays and says the module is off.
      const needs = SOLVE_WIDGET_MODULE[kind];
      if (
        !definition ||
        !definition.modes.includes("canvas") ||
        definition.storage !== "sql" ||
        (needs !== undefined && !availability.isAvailable(needs))
      ) {
        return [];
      }
      const unavailable =
        getSolveWidgetPlacementLimit(kind) === 1 &&
        (pendingSingletonKinds.has(kind) || presentKinds.has(kind));
      return [
        {
          id: kind,
          label: definition.label,
          ...(definition.subtitle ? { description: definition.subtitle } : {}),
          group:
            SOLVE_DEFAULT_PHASE[kind] === activePhase
              ? `Recommended for ${activePhase}`
              : "Other widgets",
          ...(!canAddToPhase(activePhase)
            ? { disabledReason: "This phase is locked." }
            : unavailable
              ? { disabledReason: "Already added to this SOLVE." }
              : {}),
        },
      ];
    });
  }, [
    activePhase,
    availability,
    canAddToPhase,
    pendingSingletonKinds,
    version,
    wmap,
  ]);

  const addCanvasWidget = (id: string): void => {
    const kind = id as WidgetKind;
    setPickerOpen(false);
    const limited = getSolveWidgetPlacementLimit(kind) === 1;
    if (limited && pendingSingletonKindsRef.current.has(kind)) return;
    if (limited) {
      pendingSingletonKindsRef.current.add(kind);
      setPendingSingletonKinds(new Set(pendingSingletonKindsRef.current));
    }
    const request = ++focusRequest.current;
    void addWidget(
      doc,
      kind,
      activePhase,
      solveId,
      apiConfig,
      onWidgetPlaced,
      areaGrowth,
    )
      .then((key) => {
        if (request !== focusRequest.current) return;
        const widget = wmap.get(key);
        if (!widget || !paneWidth || !paneHeight) return;
        const bounds = {
          x: widget.get("x"),
          y: widget.get("y"),
          width: widget.get("w"),
          height: widget.get("h"),
        };
        if (
          !Object.values(bounds).every(
            (value) => typeof value === "number" && Number.isFinite(value),
          )
        )
          return;
        const rectangle = bounds as {
          x: number;
          y: number;
          width: number;
          height: number;
        };
        if (rectangle.width <= 0 || rectangle.height <= 0) return;
        // Only this initiating client moves its camera; peers keep their view.
        return rf.setViewport(
          getViewportForBounds(
            rectangle,
            paneWidth,
            paneHeight,
            SOLVE_MIN_ZOOM,
            1,
            {
              top: FIT_TOP_PADDING,
              right: FIT_EDGE_PADDING,
              bottom: FIT_EDGE_PADDING,
              left: FIT_EDGE_PADDING,
            },
          ),
          { duration: framingDuration },
        );
      })
      .catch((error) => {
        if (
          error instanceof PlaybookApiError &&
          error.envelope.code === "WIDGET_SINGLETON_CONFLICT"
        ) {
          void queryClient.invalidateQueries({
            queryKey: solveWidgetsQueryKey(solveId),
            exact: true,
          });
        }
        notifyWriteError(error, queryClient, "Couldn't add widget");
      })
      .finally(() => {
        if (!limited) return;
        pendingSingletonKindsRef.current.delete(kind);
        setPendingSingletonKinds(new Set(pendingSingletonKindsRef.current));
      });
  };

  const addNote = (): void => {
    addStickyNote(doc, {
      x: clampRange((-x + 360) / zoom, 0, Math.max(0, canvasWidth - 240)),
      y: clampRange((-y + 220) / zoom, 0, canvasHeight - 180),
    });
  };
  const openSettings = (): void => {
    setPickerOpen(false);
    setMoreOpen(false);
    onOpenSettings();
  };
  const openExport = (): void => {
    setPickerOpen(false);
    setMoreOpen(false);
    onOpenExport?.();
  };
  const foldNote = density.foldLevel >= 1;
  const foldWidget = density.foldLevel >= 2;
  const foldSettings = density.foldLevel >= 3;
  const overflowItems: ActionMenuItem[] = [];
  if (onOpenExport) {
    overflowItems.push({
      id: "export-pdf",
      label: "Export PDF",
      icon: FileDown,
      onSelect: openExport,
    });
  }
  if (foldSettings) {
    overflowItems.push({
      id: "settings",
      label: "Settings",
      icon: Settings,
      onSelect: openSettings,
    });
  }
  if (foldWidget && canAddWidgets) {
    overflowItems.push({
      id: "widget",
      label: "Add widget",
      icon: SquarePlus,
      onSelect: () => {
        setMoreOpen(false);
        setPickerOpen(true);
      },
    });
  }
  if (foldNote && canAddWidgets) {
    overflowItems.push({
      id: "note",
      label: "Sticky note",
      icon: StickyNote,
      onSelect: addNote,
    });
  }
  if (onDelete) {
    overflowItems.push({
      id: "delete",
      label: "Delete SOLVE",
      icon: Trash2,
      onSelect: onDelete,
    });
  }

  const fitAll = (): void => {
    const bounds = {
      x: 0,
      y: 0,
      width: canvasWidth,
      height: canvasHeight,
    };
    if (!paneWidth || !paneHeight) {
      rf.fitBounds(bounds, { duration: framingDuration, padding: 0.08 });
      return;
    }
    rf.setViewport(
      getViewportForBounds(
        bounds,
        paneWidth,
        paneHeight,
        SOLVE_MIN_ZOOM,
        SOLVE_MAX_ZOOM,
        {
          top: FIT_TOP_PADDING,
          right: FIT_EDGE_PADDING,
          bottom: FIT_EDGE_PADDING,
          left: FIT_EDGE_PADDING,
        },
      ),
      { duration: framingDuration },
    );
  };

  return (
    <>
      <WorkspaceControlGroup
        placement="top-end"
        label="SOLVE actions"
        thresholds={SOLVE_ACTION_THRESHOLDS}
        onDensityChange={setDensity}
        data-geometry="solve-workspace-top-end"
        className="max-sm:max-w-[calc(100%-2rem)] max-sm:flex-wrap max-sm:justify-end"
        overflowSlot={
          overflowItems.length > 0 ? (
            <ActionMenu
              triggerLabel="More SOLVE actions"
              triggerSize="sm"
              // Today's 28px round trigger (no rail group here, so the
              // presence avatars and this trigger keep their own looks).
              triggerShape="circle"
              items={overflowItems}
              open={moreOpen}
              onOpenChange={(open) => {
                setMoreOpen(open);
                if (open) setPickerOpen(false);
              }}
            />
          ) : null
        }
      >
        {presenceSlot}
        {peopleSlot}
        <WorkspaceControlSlot visible={!foldSettings}>
          {/* Task 12 — these toolbar controls name themselves on hover and
              keyboard focus at every density, as the Player AI header does. */}
          <WorkspaceControlButton
            icon={<Settings className="h-4 w-4" aria-hidden />}
            label="Settings"
            alwaysShowTooltip
            onClick={openSettings}
          />
        </WorkspaceControlSlot>
        {canAddWidgets ? (
          <WorkspaceControlSlot visible={!foldWidget} retainWhenHidden>
            <WorkspaceWidgetPicker
              open={pickerOpen}
              onOpenChange={(open) => {
                setPickerOpen(open);
                if (open) setMoreOpen(false);
              }}
              items={pickerItems}
              onSelect={addCanvasWidget}
              trigger={
                <WorkspaceControlButton
                  icon={<SquarePlus className="h-4 w-4" aria-hidden />}
                  label="Add widget"
                  alwaysShowTooltip
                />
              }
            />
          </WorkspaceControlSlot>
        ) : null}
        {canAddWidgets ? (
          <WorkspaceControlSlot visible={!foldNote}>
            <WorkspaceControlButton
              icon={<StickyNote className="h-4 w-4" aria-hidden />}
              label="Sticky note"
              tooltip="Add sticky note"
              alwaysShowTooltip
              onClick={addNote}
            />
          </WorkspaceControlSlot>
        ) : null}
      </WorkspaceControlGroup>

      <WorkspaceViewportControls
        zoom={zoom}
        onZoomOut={() =>
          rf.zoomTo(clampZoom(rf.getZoom() - ZOOM_STEP), {
            duration: zoomStepDuration,
          })
        }
        onZoomIn={() =>
          rf.zoomTo(clampZoom(rf.getZoom() + ZOOM_STEP), {
            duration: zoomStepDuration,
          })
        }
        onFit={fitAll}
        label="SOLVE viewport controls"
        data-geometry="solve-workspace-bottom-start"
        data-testid="solve-canvas-toolbar"
        {...(miniMapAvailable
          ? {
              miniMap: {
                visible: miniMapVisible,
                onToggle: onToggleMiniMap,
              },
            }
          : {})}
      />
    </>
  );
}

function clampZoom(value: number): number {
  return Math.max(SOLVE_MIN_ZOOM, Math.min(SOLVE_MAX_ZOOM, value));
}

function clampRange(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}
